import { Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { combineLatest, filter, take } from 'rxjs';
import { NavigationService } from './navigation.service';
import { UserService } from './core/user';
import { EulaService } from './eula/eula.service';
import { VersionService } from '@spotlight/shared/version';
import { AccessDeniedRedirectService } from './access-denied-redirect.service';
import { PendoService } from './pendo.service';

@Injectable({
  providedIn: 'root'
})
export class OktaSetupService {
  /**
   * In-app path captured from the Okta redirect. Navigation is deferred until
   * CallbackComponent runs, after this initializer has finished user setup and
   * after Angular performs its initial navigation.
   */
  private postLoginRedirect: string | undefined;

  constructor(
    private readonly navigationService: NavigationService,
    private readonly userService: UserService,
    private readonly eulaService: EulaService,
    private readonly versionService: VersionService,
    private readonly location: Location,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly accessDeniedRedirectService: AccessDeniedRedirectService,
    private readonly pendoService: PendoService
  ) {}

  /**
   * Returns the path saved during login redirect handling, once.
   * Undefined when this load was not a login callback.
   */
  consumePostLoginRedirect(): string | undefined {
    const redirect = this.postLoginRedirect;
    this.postLoginRedirect = undefined;
    return redirect;
  }

  /**
   * Initialize the application after Okta authentication is confirmed.
   * This method waits for authentication state and then runs the setup sequence.
   */
  async initializeApp(): Promise<void> {
    try {
      const isAuthenticated = await this.oktaAuth.isAuthenticated();

      if (!isAuthenticated) {
        // If not authenticated, check if we need to handle redirect
        const isLoginRedirect = this.oktaAuth.token.isLoginRedirect();

        if (isLoginRedirect) {
          if (this.accessDeniedRedirectService.isAccessDeniedFromCurrentUrl()) {
            await this.accessDeniedRedirectService.navigateToAccessDenied();
            return;
          }

          // Store tokens here, but do not navigate. Okta's default restoreOriginalUri
          // calls Router.navigateByUrl during this initializer, which counts as a
          // requested navigation. Angular then skips its initial navigation, the
          // callback route never activates, and the shell stays blank until reload.
          // The guards would also cancel that navigation because user setup has not
          // run yet, and handleLoginRedirect clears the stored original URI.
          this.oktaAuth.options.restoreOriginalUri = (_oktaAuth, originalUri) => {
            this.postLoginRedirect = this.toAppPath(originalUri);
            return Promise.resolve();
          };
          await this.oktaAuth.handleLoginRedirect();
        }
      }

      // At this point, user is authenticated - proceed with app setup
      await this.runSetup();
    } catch (error) {
      if (this.accessDeniedRedirectService.isAccessDeniedFromCurrentUrl()) {
        await this.accessDeniedRedirectService.navigateToAccessDenied();
        return;
      }

      const errorObj = error instanceof Error ? error : new Error(String(error));
      console.error('[OktaSetupService] Okta setup initialization error:', errorObj);
      console.error('[OktaSetupService] Error stack:', errorObj.stack);
      throw errorObj;
    }
  }

  /**
   * Run the application setup sequence after authentication is confirmed.
   * User + EULA run in parallel (EULA only needs the auth token, not /api/user).
   * Pendo is deferred until the browser is idle so it does not compete with first paint.
   */
  private async runSetup(): Promise<void> {
    try {
      const version = this.versionService.getVersion();
      const navResult = await this.navigationService.setup();

      if (navResult) {
        await Promise.all([
          this.userService.setupUser(version),
          this.eulaService.getEula()
        ]);
        this.schedulePendoInitialization();
      } else {
        console.warn('[OktaSetupService] Navigation setup returned false, skipping user and EULA setup');
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      console.error('[OktaSetupService] Setup error:', errorObj);
      console.error('[OktaSetupService] Setup error stack:', errorObj.stack);
      throw errorObj;
    }
  }

  /**
   * Defer Pendo until after the critical startup path so analytics does not
   * compete with first render. Falls back to setTimeout when idle callbacks
   * are unavailable.
   */
  private schedulePendoInitialization(): void {
    const startPendo = () => {
      combineLatest([
        this.userService.getUser(),
        this.userService.getSelectedLocation()
      ]).pipe(
        filter(([user]) => !!user),
        take(1)
      ).subscribe(([user, location]) => {
        if (user) {
          void this.pendoService.initialize(user, location);
        }
      });
    };

    // Keep the method on its receiver — detaching requestIdleCallback throws Illegal invocation.
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => startPendo(), { timeout: 2000 });
    } else {
      setTimeout(startPendo, 0);
    }
  }

  /**
   * Turns the URI Okta stored before sign-in into an in-app path.
   * Absolute URLs are accepted only when they match this app's origin.
   */
  private toAppPath(originalUri: string | undefined): string | undefined {
    if (!originalUri) {
      return undefined;
    }

    const baseUrl = (globalThis.location.origin + this.location.prepareExternalUrl('')).replace(/\/+$/, '');
    let routePath = originalUri;
    if (/^[a-z][a-z0-9+.-]*:/i.test(routePath)) {
      if (!routePath.startsWith(baseUrl)) {
        return undefined;
      }
      routePath = routePath.substring(baseUrl.length);
    }

    if (!routePath.startsWith('/')) {
      routePath = `/${routePath}`;
    }

    if (routePath.startsWith('//') || routePath === '/') {
      return undefined;
    }

    return routePath;
  }
}
